import { useEffect, useMemo, useState } from "react";
import { useLanguage, useT } from "../i18n/hooks.ts";
import { HELP_BY_LANGUAGE, searchHelp, CATEGORIES_BY_LANGUAGE } from "../i18n/help.ts";

/**
 * Help available at any time (the "?" icon in the top bar): a searchable
 * summary of what the app can do — not CLAUDE.md's technical
 * documentation, but the user-facing one. A large popover with a blurred
 * background instead of a regular modal dialog (Dialog.tsx): this is
 * consulted in passing, without necessarily cutting off whatever was being
 * done, so the background is left visible.
 */
export function HelpPanel({ onClose }: { onClose: () => void }) {
  const t = useT();
  const language = useLanguage();
  const HELP = HELP_BY_LANGUAGE[language];
  const HELP_CATEGORIES = CATEGORIES_BY_LANGUAGE[language];
  const [query, setQuery] = useState("");
  const results = useMemo(() => searchHelp(HELP, query), [HELP, query]);
  const byCategory = useMemo(
    () =>
      HELP_CATEGORIES.map((category) => ({
        category,
        entries: results.filter((e) => e.category === category),
      })).filter((g) => g.entries.length > 0),
    [HELP_CATEGORIES, results]
  );

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className="help-backdrop" onClick={onClose}>
      <div className="help-panel" onClick={(e) => e.stopPropagation()}>
        <div className="help-header">
          <h2>{t("helpPanel.title")}</h2>
          <button className="button-icon" onClick={onClose} aria-label={t("helpPanel.closeAria")}>
            ✕
          </button>
        </div>

        <input
          autoFocus
          type="search"
          className="help-search"
          placeholder={t("helpPanel.searchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className="help-body">
          {byCategory.length === 0 && (
            <p className="map-notice">{t("helpPanel.noResults", { query })}</p>
          )}
          {byCategory.map((group) => (
            <section key={group.category} className="help-category">
              <h3>{group.category}</h3>
              {group.entries.map((entry) => (
                <article key={entry.id} className="help-entry">
                  <h4>{entry.title}</h4>
                  {entry.content.map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </article>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
